import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { quotationsAPI, quotationItemsAPI, clientsAPI, suppliersAPI, quotationSuppliersAPI } from '../api/endpoints.js'
import { useToast } from '../contexts/ToastContext'
import StatusBadge from '../components/ui/StatusBadge'
import ExpirationCountdown from '../components/ui/ExpirationCountdown'
import SuppliersProgress from '../components/ui/SuppliersProgress'
import StatusTimeline from '../components/ui/StatusTimeline'

function QuotationDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [quotation, setQuotation] = useState(null)
  const [items, setItems] = useState([])
  const [client, setClient] = useState(null)
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [previousStatus, setPreviousStatus] = useState(null)

  // Auto-refresh quotation details every 15 seconds
  useEffect(() => {
    loadQuotationDetails()
    
    const interval = setInterval(() => {
      loadQuotationDetails(true) // Silent refresh
    }, 15000)
    
    return () => clearInterval(interval)
  }, [id])

  const loadQuotationDetails = async (silentRefresh = false) => {
    try {
      if (!silentRefresh) {
        setLoading(true)
      }
      
      // Carregar dados da cotação
      const quotationRes = await quotationsAPI.getById(id)
      const quotationData = quotationRes.data
      
      // Check for status changes and notify
      if (previousStatus && quotationData.quotation_status_id !== previousStatus) {
        const statusLabels = {
          1: 'Rascunho',
          2: 'Ativa', 
          3: 'Fechada',
          4: 'Cancelada',
          5: 'Expirada'
        }
        addToast(`Status Atualizado: Cotação #${id} foi ${statusLabels[quotationData.quotation_status_id]}`, 'info')
      }
      
      setPreviousStatus(quotationData.quotation_status_id)
      setQuotation(quotationData)

      // Carregar itens da cotação
      const itemsRes = await quotationItemsAPI.getByQuotationId(id)
      setItems(itemsRes.data || [])

      // Carregar dados do cliente
      if (quotationData.client_id) {
        const clientRes = await clientsAPI.getById(quotationData.client_id)
        setClient(clientRes.data)
      }

      // Carregar fornecedores da cotação
      const suppliersRes = await quotationSuppliersAPI.getByQuotationId(id)
      console.log('=== FORNECEDORES DA COTAÇÃO ===')
      console.log('Resposta completa:', suppliersRes)
      console.log('Fornecedores:', suppliersRes.data)
      console.log('Primeiro fornecedor completo:', JSON.stringify(suppliersRes.data?.[0], null, 2))
      console.log('==============================')
      
      // Buscar dados completos dos fornecedores
      const suppliersWithData = await Promise.all(
        (suppliersRes.data || []).map(async (qs) => {
          try {
            const supplierData = await suppliersAPI.getById(qs.supplier_id)
            return {
              ...qs,
              supplier: supplierData.data
            }
          } catch (error) {
            console.error(`Erro ao carregar fornecedor ${qs.supplier_id}:`, error)
            return qs
          }
        })
      )
      
      console.log('Fornecedores com dados completos:', suppliersWithData)
      setSuppliers(suppliersWithData)

    } catch (error) {
      console.error('Error loading quotation details:', error)
      if (!silentRefresh) {
        addToast('Erro ao carregar detalhes da cotação', 'error')
      }
    } finally {
      if (!silentRefresh) {
        setLoading(false)
      }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'bg-yellow-100 text-yellow-800' // Pendente
      case 2: return 'bg-blue-100 text-blue-800'     // Em andamento
      case 3: return 'bg-green-100 text-green-800'   // Concluída
      case 4: return 'bg-red-100 text-red-800'       // Expirada
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'Pendente'
      case 2: return 'Em Andamento'
      case 3: return 'Concluída'
      case 4: return 'Expirada'
      default: return 'Desconhecido'
    }
  }

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getUrgencyText = (urgency) => {
    switch (urgency) {
      case 'high': return 'Alta'
      case 'medium': return 'Média'
      case 'low': return 'Baixa'
      default: return 'Normal'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-dark-accent-green mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-dark-text-secondary">Carregando detalhes da cotação...</p>
        </div>
      </div>
    )
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary flex items-center justify-center transition-colors">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-dark-text-primary mb-2">Cotação não encontrada</h2>
          <p className="text-gray-600 dark:text-dark-text-secondary mb-4">A cotação solicitada não existe ou você não tem permissão para visualizá-la.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 dark:bg-dark-accent-green text-white rounded-md hover:bg-blue-700 dark:hover:bg-dark-accent-greenDark transition-colors"
          >
            Voltar ao Início
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary py-8 transition-colors">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 mb-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/app')}
              className="flex items-center text-blue-600 dark:text-dark-accent-green hover:text-blue-700 dark:hover:text-dark-accent-greenLight transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Voltar
            </button>
            <div className="flex items-center space-x-4">
              <StatusBadge status={quotation.quotation_status_id} type="quotation" />
              {quotation.quotation_status_id !== 5 && quotation.quotation_status_id !== 4 && quotation.quotation_status_id !== 3 && (
                <ExpirationCountdown expirationDate={quotation.expiration_date} />
              )}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-800 dark:text-dark-text-primary mb-2">
            Cotação #{quotation.id}
          </h1>
          <p className="text-gray-600 dark:text-dark-text-secondary">
            {client ? `Cliente: ${client.name}` : 'Carregando dados do cliente...'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Informações Principais */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Timeline */}
            <StatusTimeline quotation={quotation} />
            {/* Detalhes da Cotação */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Detalhes da Cotação</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Data de Solicitação</label>
                  <p className="text-gray-900 dark:text-dark-text-primary">{new Date(quotation.request_date).toLocaleDateString('pt-BR')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Data de Expiração</label>
                  <p className="text-gray-900 dark:text-dark-text-primary">{new Date(quotation.expiration_date).toLocaleDateString('pt-BR')}</p>
                </div>
                {(quotation.quotation_status_id === 3 || quotation.quotation_status_id === 5) && quotation.updated_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
                      {quotation.quotation_status_id === 3 ? 'Fechada em' : 'Expirada em'}
                    </label>
                    <p className="text-gray-900 dark:text-dark-text-primary">
                      {new Date(quotation.updated_at).toLocaleDateString('pt-BR')} às {new Date(quotation.updated_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                )}
                {quotation.delivery_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Data de Entrega</label>
                    <p className="text-gray-900 dark:text-dark-text-primary">{new Date(quotation.delivery_date).toLocaleDateString('pt-BR')}</p>
                  </div>
                )}
                {quotation.delivery_location && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Local de Entrega</label>
                    <p className="text-gray-900 dark:text-dark-text-primary">{quotation.delivery_location}</p>
                  </div>
                )}
              </div>
              
              {quotation.notes && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Observações</label>
                  <p className="text-gray-900 dark:text-dark-text-primary bg-gray-50 dark:bg-dark-bg-tertiary p-3 rounded-md">{quotation.notes}</p>
                </div>
              )}
            </div>

            {/* Itens da Cotação */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Itens Solicitados</h2>
              {items.length === 0 ? (
                <p className="text-gray-500 dark:text-dark-text-muted">Nenhum item encontrado para esta cotação.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border-primary">
                    <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                          Quantidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                          Unidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">
                          Observações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border-primary">
                      {items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                            {item.item_name || `Item #${item.item_id}`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">
                            {item.unit_name || 'Un'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-dark-text-secondary">
                            {item.notes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Suppliers Progress */}
            <SuppliersProgress suppliers={suppliers} />

            {/* Informações do Cliente */}
            {client && (
              <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Cliente</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-700 dark:text-dark-text-secondary"><strong className="text-gray-900 dark:text-dark-text-primary">Nome:</strong> {client.name}</p>
                  {client.email && <p className="text-sm text-gray-700 dark:text-dark-text-secondary"><strong className="text-gray-900 dark:text-dark-text-primary">Email:</strong> {client.email}</p>}
                  {client.phone && <p className="text-sm text-gray-700 dark:text-dark-text-secondary"><strong className="text-gray-900 dark:text-dark-text-primary">Telefone:</strong> {client.phone}</p>}
                  {client.document && <p className="text-sm text-gray-700 dark:text-dark-text-secondary"><strong className="text-gray-900 dark:text-dark-text-primary">Documento:</strong> {client.document}</p>}
                </div>
              </div>
            )}

            {/* Fornecedores Detalhados */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Fornecedores</h3>
              {suppliers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-dark-text-muted">Nenhum fornecedor associado ainda.</p>
              ) : (
                <div className="space-y-3">
                  {suppliers.map((supplier) => {
                    const isExpired = quotation.quotation_status_id === 5
                    const isPending = supplier.quotation_supplier_status_id === 1
                    const showNotResponded = isExpired && isPending
                    
                    return (
                      <div key={supplier.id} className="p-3 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg border border-gray-200 dark:border-dark-border-primary">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">{supplier.supplier?.trade_name || supplier.supplier?.legal_name || 'Fornecedor'}</p>
                          {showNotResponded ? (
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300">
                              ❌ Não respondido
                            </span>
                          ) : (
                            <StatusBadge status={supplier.quotation_supplier_status_id} type="supplier" />
                          )}
                        </div>
                        {supplier.supplier?.email && (
                          <p className="text-xs text-gray-600 dark:text-dark-text-muted">📧 {supplier.supplier.email}</p>
                        )}
                        {supplier.supplier?.phone && (
                          <p className="text-xs text-gray-600 dark:text-dark-text-muted">📞 {supplier.supplier.phone}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md dark:shadow-none p-6 border border-gray-200 dark:border-dark-border-primary transition-colors">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-dark-text-primary mb-4">Ações</h3>
              <div className="space-y-2">
                <button className="w-full px-4 py-2 bg-blue-600 dark:bg-dark-accent-green text-white text-sm rounded-md hover:bg-blue-700 dark:hover:bg-dark-accent-greenDark transition-colors">
                  Editar Cotação
                </button>
                <button className="w-full px-4 py-2 bg-green-600 dark:bg-green-700 text-white text-sm rounded-md hover:bg-green-700 dark:hover:bg-green-800 transition-colors">
                  Enviar para Fornecedores
                </button>
                <button className="w-full px-4 py-2 bg-yellow-600 dark:bg-yellow-700 text-white text-sm rounded-md hover:bg-yellow-700 dark:hover:bg-yellow-800 transition-colors">
                  Gerar Relatório
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuotationDetails
